import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  RefreshCw, Eye, CheckCircle2, XCircle, MessageSquareText,
  FileText, Image, Video, Phone, ExternalLink, Zap, Tag,
  ArrowUpCircle, ToggleLeft, ToggleRight, Lock,
} from "lucide-react";

// ── helpers ────────────────────────────────────────────────────────────────
function metaBadge(metaStatus) {
  if (metaStatus === "APPROVED") return "bg-emerald-500/15 text-emerald-500 border-emerald-500/20";
  if (metaStatus === "REJECTED") return "bg-red-500/15 text-red-500 border-red-500/20";
  if (metaStatus === "PENDING")  return "bg-blue-500/15 text-blue-400 border-blue-500/20";
  return "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
}

// ── main component ─────────────────────────────────────────────────────────
export default function WhatsAppTemplates() {
  const [templates,      setTemplates]      = useState([]);   // org mappings enriched
  const [loading,        setLoading]        = useState(true);
  const [tab,            setTab]            = useState("enabled");

  // per-row action loading  { [orgTemplateId]: "sync" | "toggle" }
  const [rowLoading,     setRowLoading]     = useState({});
  const [syncAllLoading, setSyncAllLoading] = useState(false);

  // preview
  const [preview,        setPreview]        = useState(null);

  // ── fetch ──────────────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      // Returns org-level template mappings that are based on published admin templates.
      // Expected shape per item:
      // { orgTemplateId, templateId, name, category, language, automationEvent,
      //   versionTag, metaStatus, isEnabled, hasUpgrade,
      //   content: { headerType, headerText, body, footer, buttons } }
      const res = await api.get("/whatsapp/org-templates");
      setTemplates(res.data.templates || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ── toggle enable/disable ──────────────────────────────────────────────
  const handleToggle = async (t) => {
    setRowLoading(r => ({ ...r, [t.orgTemplateId]: "toggle" }));
    try {
      await api.patch(`/whatsapp/org-templates/${t.orgTemplateId}/toggle`);
      toast.success(t.isEnabled ? "Template disabled" : "Template enabled");
      fetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setRowLoading(r => { const n = { ...r }; delete n[t.orgTemplateId]; return n; }); }
  };

  // ── sync single ────────────────────────────────────────────────────────
  const handleSync = async (t) => {
    setRowLoading(r => ({ ...r, [t.orgTemplateId]: "sync" }));
    try {
      await api.post(`/whatsapp/org-templates/${t.orgTemplateId}/sync`);
      toast.success("Synced to latest version");
      fetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setRowLoading(r => { const n = { ...r }; delete n[t.orgTemplateId]; return n; }); }
  };

  // ── sync all ───────────────────────────────────────────────────────────
  const handleSyncAll = async () => {
    setSyncAllLoading(true);
    try {
      const res = await api.post("/whatsapp/org-templates/sync-all");
      toast.success(res.data.message || "All templates synced");
      fetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync all failed"); }
    finally { setSyncAllLoading(false); }
  };

  // ── derived lists ──────────────────────────────────────────────────────
  const enabledTemplates  = templates.filter(t => t.isEnabled);
  const upgradeCount      = templates.filter(t => t.hasUpgrade).length;

  const displayed = tab === "enabled" ? enabledTemplates : templates;

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight uppercase">Message Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Published templates from the master library</p>
        </div>
        <div className="flex items-center gap-2">
          {upgradeCount > 0 && (
            <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/20 text-xs font-mono animate-pulse">
              <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />{upgradeCount} upgrade{upgradeCount > 1 ? "s" : ""} available
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncAll}
            disabled={syncAllLoading}
            className="font-heading uppercase tracking-wide text-xs"
          >
            {syncAllLoading
              ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync All
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "TOTAL",    value: templates.length,       color: "",                   icon: MessageSquareText },
          { label: "ENABLED",  value: enabledTemplates.length, color: "text-emerald-500",   icon: CheckCircle2 },
          { label: "UPGRADES", value: upgradeCount,            color: "text-blue-400",      icon: ArrowUpCircle },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-3 sm:p-4">
              <s.icon className={`h-4 w-4 ${s.color || "text-muted-foreground"}`} />
              <p className={`text-2xl font-black font-heading mt-1.5 ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-9">
          <TabsTrigger value="enabled" className="text-xs font-heading uppercase tracking-wide px-5">
            Enabled
            {enabledTemplates.length > 0 && (
              <span className="ml-2 bg-emerald-500/20 text-emerald-500 text-[10px] font-mono px-1.5 py-0.5 rounded">
                {enabledTemplates.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="all" className="text-xs font-heading uppercase tracking-wide px-5">
            All Templates
            {templates.length > 0 && (
              <span className="ml-2 bg-secondary text-muted-foreground text-[10px] font-mono px-1.5 py-0.5 rounded">
                {templates.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {["enabled", "all"].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey} className="mt-4">
            <TemplateGrid
              templates={displayed}
              loading={loading}
              rowLoading={rowLoading}
              onToggle={handleToggle}
              onSync={handleSync}
              onPreview={setPreview}
              emptyLabel={tabKey === "enabled" ? "No templates enabled yet." : "No published templates available."}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" />{preview?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {preview && <TemplatePreviewCard template={preview} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Template Grid ───────────────────────────────────────────────────────────
function TemplateGrid({ templates, loading, rowLoading, onToggle, onSync, onPreview, emptyLabel }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (templates.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-16 text-center">
          <MessageSquareText className="h-10 w-10 mx-auto mb-3 opacity-25" />
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {templates.map(t => (
        <TemplateCard
          key={t.orgTemplateId}
          t={t}
          rowLoading={rowLoading}
          onToggle={onToggle}
          onSync={onSync}
          onPreview={onPreview}
        />
      ))}
    </div>
  );
}

// ── Template Card ───────────────────────────────────────────────────────────
function TemplateCard({ t, rowLoading, onToggle, onSync, onPreview }) {
  const isToggling = rowLoading[t.orgTemplateId] === "toggle";
  const isSyncing  = rowLoading[t.orgTemplateId] === "sync";
  const busy       = !!rowLoading[t.orgTemplateId];

  return (
    <Card className={`bg-card border-border transition-colors ${
      t.isEnabled ? "hover:border-primary/40" : "opacity-60 hover:opacity-80"
    }`}>
      <CardContent className="p-4 space-y-3">

        {/* Top row: name + version + meta badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-semibold text-sm font-mono truncate">{t.name}</p>
              {t.versionTag && (
                <Badge variant="outline" className="text-[10px] font-mono shrink-0">{t.versionTag}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Badge variant="outline" className="text-[10px] font-mono uppercase">{t.category}</Badge>
              <Badge variant="outline" className="text-[10px] font-mono">{t.language}</Badge>
              {t.automationEvent && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-amber-500">
                  <Zap className="h-2.5 w-2.5" />{t.automationEvent}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {t.metaStatus && (
              <Badge className={`${metaBadge(t.metaStatus)} border text-[9px] font-mono uppercase`}>{t.metaStatus}</Badge>
            )}
            {t.isEnabled
              ? <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/20 text-[9px] font-mono uppercase">Enabled</Badge>
              : <Badge className="bg-zinc-500/15 text-zinc-400 border border-zinc-500/20 text-[9px] font-mono uppercase">Disabled</Badge>
            }
          </div>
        </div>

        {/* Header indicator */}
        {t.content?.headerType && t.content.headerType !== "NONE" && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {t.content.headerType === "IMAGE"    && <Image    className="h-3.5 w-3.5" />}
            {t.content.headerType === "VIDEO"    && <Video    className="h-3.5 w-3.5" />}
            {t.content.headerType === "DOCUMENT" && <FileText className="h-3.5 w-3.5" />}
            {t.content.headerType === "TEXT"     && <FileText className="h-3.5 w-3.5" />}
            <span className="capitalize">{t.content.headerType.toLowerCase()} header</span>
            {t.content.headerType === "TEXT" && t.content.headerText && (
              <span className="text-muted-foreground/70 truncate max-w-[120px]">· {t.content.headerText}</span>
            )}
          </div>
        )}

        {/* Body snippet */}
        <div className="p-2.5 bg-secondary/30 rounded-lg">
          <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
            {t.content?.body || "No message body."}
          </p>
        </div>

        {/* Variables */}
        {t.variables?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {t.variables.map(v => (
              <span key={v} className="inline-flex items-center gap-0.5 bg-primary/10 text-primary text-[10px] font-mono px-1.5 py-0.5 rounded">
                <Tag className="h-2.5 w-2.5" />{v}
              </span>
            ))}
          </div>
        )}

        {/* Upgrade banner */}
        {t.hasUpgrade && (
          <div className="flex items-center gap-2 rounded-md bg-blue-500/10 border border-blue-500/20 px-2.5 py-1.5">
            <ArrowUpCircle className="h-3.5 w-3.5 text-blue-400 shrink-0" />
            <p className="text-[11px] text-blue-400">Newer version available — sync to upgrade</p>
          </div>
        )}

        <Separator />

        {/* Action row */}
        <div className="flex items-center gap-1.5">
          {/* Preview */}
          <Button
            variant="ghost" size="sm"
            className="h-7 text-[11px] font-heading uppercase flex-1"
            onClick={() => onPreview(t)}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />Preview
          </Button>

          {/* Enable / Disable toggle */}
          <Button
            variant="ghost" size="sm"
            className={`h-7 text-[11px] font-heading uppercase flex-1 ${
              t.isEnabled
                ? "text-rose-500 hover:text-rose-400"
                : "text-emerald-500 hover:text-emerald-400"
            }`}
            disabled={busy}
            onClick={() => onToggle(t)}
          >
            {isToggling
              ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
              : t.isEnabled
                ? <><ToggleRight className="h-3.5 w-3.5 mr-1" />Disable</>
                : <><ToggleLeft  className="h-3.5 w-3.5 mr-1" />Enable</>}
          </Button>

          {/* Sync Now */}
          <Button
            variant="ghost" size="sm"
            className="h-7 text-[11px] font-heading uppercase text-blue-400 hover:text-blue-300"
            disabled={busy}
            onClick={() => onSync(t)}
            title="Sync to latest version"
          >
            {isSyncing
              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Preview Card ─────────────────────────────────────────────────────────────
function TemplatePreviewCard({ template: t }) {
  const content = t.content || {};
  return (
    <div className="space-y-4">
      {/* Meta info */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="font-mono text-xs uppercase">{t.category}</Badge>
        <Badge variant="outline" className="font-mono text-xs">{t.language}</Badge>
        {t.versionTag && (
          <Badge variant="outline" className="font-mono text-xs"><Lock className="h-3 w-3 mr-1" />{t.versionTag}</Badge>
        )}
        {t.metaStatus && (
          <Badge className={`${metaBadge(t.metaStatus)} border text-xs font-mono uppercase`}>{t.metaStatus}</Badge>
        )}
        {t.automationEvent && (
          <Badge className="bg-amber-500/15 text-amber-500 border border-amber-500/20 text-xs font-mono">
            <Zap className="h-3 w-3 mr-1" />{t.automationEvent}
          </Badge>
        )}
      </div>

      {/* WhatsApp bubble */}
      <div className="max-w-[280px] mx-auto">
        <div className="bg-[#DCF8C6] dark:bg-[#025C4C] rounded-lg rounded-tl-none p-3 space-y-2 shadow-sm">
          {/* Header */}
          {content.headerType === "IMAGE" && (
            <div className="w-full h-28 bg-zinc-200 dark:bg-zinc-700 rounded flex items-center justify-center">
              <Image className="h-7 w-7 text-zinc-400" />
            </div>
          )}
          {content.headerType === "VIDEO" && (
            <div className="w-full h-28 bg-zinc-200 dark:bg-zinc-700 rounded flex items-center justify-center">
              <Video className="h-7 w-7 text-zinc-400" />
            </div>
          )}
          {content.headerType === "DOCUMENT" && (
            <div className="w-full h-16 bg-zinc-200 dark:bg-zinc-700 rounded flex items-center justify-center gap-2">
              <FileText className="h-6 w-6 text-zinc-400" />
              <span className="text-xs text-zinc-500">Document</span>
            </div>
          )}
          {content.headerType === "TEXT" && content.headerText && (
            <p className="font-bold text-sm text-zinc-900 dark:text-white">{content.headerText}</p>
          )}
          {/* Body */}
          <p className="text-sm text-zinc-800 dark:text-zinc-100 whitespace-pre-wrap">
            {content.body || "Message body will appear here..."}
          </p>
          {/* Footer */}
          {content.footer && (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{content.footer}</p>
          )}
          <div className="flex justify-end">
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>
        {/* Buttons */}
        {content.buttons?.length > 0 && (
          <div className="mt-1 space-y-1">
            {content.buttons.map((btn, i) => (
              <div key={i}
                className="w-full py-2 text-center text-sm text-[#00A884] dark:text-[#25D366] bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg flex items-center justify-center gap-2">
                {btn.type === "CALL" ? <Phone className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                {btn.text || `Button ${i + 1}`}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Variables */}
      {t.variables?.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Variables</p>
          <div className="flex flex-wrap gap-1">
            {t.variables.map(v => (
              <span key={v} className="inline-flex items-center gap-0.5 bg-primary/10 text-primary text-[10px] font-mono px-1.5 py-0.5 rounded">
                <Tag className="h-2.5 w-2.5" />{v}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
